// client/src/components/Modal.jsx
// Lightweight overlay modal. Backdrop click + Escape key close it.
// Children are responsible for their own padding (we provide a default
// in the body wrapper).
//
// Backdrop close uses mousedown + mouseup tracking instead of onClick.
// Reason: if the user starts a text selection INSIDE the modal and drags
// the mouse outside before releasing, a click event fires on the backdrop
// (its target is the lowest common ancestor of down + up). Tracking both
// events and checking they BOTH happened on the backdrop fixes the
// "drag-out-of-modal closed it by accident" bug.
//
// isDirty prop: when true, X / backdrop / Escape all confirm before
// closing, so the user can't lose typed-but-unsaved form input by
// misclick. Parents that don't care about that pass nothing.

import { useEffect, useRef } from 'react';

export default function Modal({
  open,
  onClose,
  title,
  children,
  maxWidth = 'max-w-lg',
  isDirty = false,
}) {
  const downOnBackdropRef = useRef(false);

  function tryClose() {
    if (isDirty) {
      const ok = window.confirm('יש שינויים שלא נשמרו.\nלסגור בלי לשמור?');
      if (!ok) return;
    }
    onClose?.();
  }

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') tryClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
    // tryClose captures isDirty via closure — re-bind when either changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isDirty]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-end sm:place-items-center bg-ink-900/40 sm:p-4 backdrop-blur-md animate-fade-in"
      role="presentation"
      onMouseDown={(e) => {
        downOnBackdropRef.current = e.target === e.currentTarget;
      }}
      onMouseUp={(e) => {
        // Only close when BOTH events landed on the backdrop. A drag that
        // started inside the modal (down on inner card) has the flag false.
        if (downOnBackdropRef.current && e.target === e.currentTarget) {
          tryClose();
        }
        downOnBackdropRef.current = false;
      }}
    >
      <div
        className={`card-pop w-full ${maxWidth} max-h-[92vh] flex flex-col
                    rounded-b-none sm:rounded-4xl animate-slide-up`}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink-100">
          <h2 className="text-lg font-bold text-ink-900">{title}</h2>
          <button
            type="button"
            onClick={tryClose}
            className="text-ink-400 hover:text-ink-900 w-9 h-9 grid place-items-center rounded-xl hover:bg-ink-100 transition-colors"
            aria-label="סגור"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="px-6 py-5 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}
